import React, { ReactNode } from "react";
import { useModuleEntitled } from "../../hooks/useModuleEntitled";
import { Tier } from "../../api/entitlementsAPI";

interface ModuleGateProps {
  /** module_key from platform_feature_categories (e.g. "ai_phone_receptionist"). */
  moduleKey: string;
  /** Rendered when the tenant has the module. */
  children: ReactNode;
  /**
   * Custom fallback when access is denied. If omitted, the gate renders
   * a default upgrade prompt for soft-gated modules and renders null
   * for hard-gated / frontend_only modules.
   */
  fallback?: ReactNode;
}

/**
 * Wrap a section of UI that requires a specific module. Reads the
 * tenant's entitlements from EntitlementContext (loaded once at boot
 * via /api/tenant/entitlements) and renders children when granted.
 *
 * Behavior on denial follows the module's gating mode:
 *   - "hard"           → render nothing (hide entirely)
 *   - "soft"           → render fallback (default: upgrade prompt)
 *   - "frontend_only"  → render nothing
 *   - "marketing_only" → render children anyway (no runtime gate)
 *   - unknown key      → render nothing (denied with gating "hard", as
 *                        the backend's requireModule gates it)
 *
 * Backend's requireModule middleware is the actual security boundary;
 * this component is a UX layer. See docs/FEATURE_ENTITLEMENTS.md §4.
 *
 * Usage:
 *   <ModuleGate moduleKey="local_seo">
 *     <LocalSeoDashboard />
 *   </ModuleGate>
 *
 *   // Custom fallback:
 *   <ModuleGate moduleKey="ai_phone_receptionist" fallback={<UpsellCard />}>
 *     <AIReceptionistSettings />
 *   </ModuleGate>
 */
export const ModuleGate: React.FC<ModuleGateProps> = ({
  moduleKey,
  children,
  fallback,
}) => {
  const result = useModuleEntitled(moduleKey);

  if (result.granted) return <>{children}</>;
  if (result.gating === "marketing_only") return <>{children}</>;
  if (result.gating === "hard" || result.gating === "frontend_only") return null;

  // gating === 'soft' (default for premium modules) — show the fallback
  // or the default upgrade prompt to drive organic upgrades.
  if (fallback !== undefined) return <>{fallback}</>;

  return (
    <DefaultUpgradePrompt
      requiredTier={result.requiredTier}
      requiredAddon={result.requiredAddon}
      moduleName={result.module?.name}
    />
  );
};

interface DefaultUpgradePromptProps {
  requiredTier?: Tier | null;
  requiredAddon?: string | null;
  moduleName?: string;
}

/**
 * Default upgrade overlay for soft-gated modules. Intentionally minimal
 * — apps with stronger UX requirements should pass a custom `fallback`
 * to <ModuleGate>. Kept inline so a tenant frontend without a design
 * system still gets a sensible default.
 */
const DefaultUpgradePrompt: React.FC<DefaultUpgradePromptProps> = ({
  requiredTier,
  requiredAddon,
  moduleName,
}) => {
  const what = moduleName || "This feature";
  let detail = "is not included in your current plan.";
  if (requiredAddon) {
    detail = `requires the ${requiredAddon.replace(/_/g, " ")} add-on.`;
  } else if (requiredTier) {
    detail = `requires the ${requiredTier} plan or higher.`;
  }

  return (
    <div
      role="region"
      aria-label="Feature requires upgrade"
      style={{
        padding: "1rem",
        border: "1px dashed #d1d5db",
        borderRadius: "0.5rem",
        background: "#f9fafb",
        color: "#374151",
        textAlign: "center",
      }}
    >
      <p style={{ margin: 0, fontWeight: 600 }}>
        {what} {detail}
      </p>
      <p style={{ margin: "0.5rem 0 0", fontSize: "0.875rem", color: "#6b7280" }}>
        Visit billing settings to upgrade.
      </p>
    </div>
  );
};

export default ModuleGate;
